import React from 'react';
import { resolveSlotComponent } from '../ComponentRegistry.jsx';
import { PortalSearchHero } from '../components/heroes/PortalSearchHero.jsx';
import { HeroBackdrop, HeroBackdropEditPill } from '../../components/editing/HeroBackdrop.jsx';
import { useContentBlocks, resolveContentValue, CONTENT_FALLBACKS } from '../../lib/content.js';
import { useEditMode } from '../../context/EditModeContext.jsx';
import { EditableText } from '../../components/editing/EditableText.jsx';

export function HeroSlot({
  variant,
  tenant,
  catalogCount = null,
  onSearch,
  onSelectCategory,
  onNavigate,
  featuredProduct = null,
  categories,
  backdropUrl = null,
  backdropStrength = 35,
  heroTitle,
  heroSubtitle,
  heroCtaPrimary,
  heroCtaSecondary,
  contentMap: passedContentMap,
  ...props
}) {
  const requestedVariant =
    variant ||
    tenant?.settings?.layout_hero ||
    tenant?.layout_hero ||
    'portal_search';

  const HeroComponent = resolveSlotComponent('hero', requestedVariant) || PortalSearchHero;

  let editContext = { revision: 0 };
  try {
    editContext = useEditMode() || editContext;
  } catch {
    // isolated test without EditModeProvider
  }

  const fetchedBlocks = useContentBlocks(
    'home',
    tenant?.slug,
    { enabled: !passedContentMap && (heroCtaPrimary === undefined || heroCtaSecondary === undefined) && Boolean(tenant?.slug), revision: editContext?.revision || 0 }
  );
  const contentMap = passedContentMap || fetchedBlocks?.map || {};

  const heroCtaPrimaryRaw = resolveContentValue(contentMap, 'hero_cta_primary', '');
  const heroCtaSecondaryRaw = resolveContentValue(contentMap, 'hero_cta_secondary', '');

  const isHighlightGrid = requestedVariant === 'highlight_grid';
  const defaultPrimaryCta = isHighlightGrid ? 'Gesamten Katalog öffnen' : CONTENT_FALLBACKS.home.hero_cta_primary;
  const defaultSecondaryCta = isHighlightGrid ? 'Projektangebot anfragen' : CONTENT_FALLBACKS.home.hero_cta_secondary;

  const resolvedHeroCtaPrimary = heroCtaPrimary !== undefined ? heroCtaPrimary : (
    <EditableText page="home" blockKey="hero_cta_primary" value={heroCtaPrimaryRaw} data-testid="edit-hero-cta-primary">
      {heroCtaPrimaryRaw || defaultPrimaryCta}
    </EditableText>
  );

  const resolvedHeroCtaSecondary = heroCtaSecondary !== undefined ? heroCtaSecondary : (
    <EditableText page="home" blockKey="hero_cta_secondary" value={heroCtaSecondaryRaw} data-testid="edit-hero-cta-secondary">
      {heroCtaSecondaryRaw || defaultSecondaryCta}
    </EditableText>
  );

  // Hintergrundbild (Issue #3 Paket B): Backdrop hinter der Variante. Der Wrapper
  // bleibt immer im Baum (stabile Struktur beim Umschalten); data-hero-backdrop
  // steuert per CSS, ob die Fläche der Variante transparent wird.
  return (
    <div
      data-testid="hero-slot"
      data-hero-backdrop={backdropUrl ? 'on' : 'off'}
      className="relative isolate rounded-3xl anim-hero-entrance"
    >
      <HeroBackdrop imageUrl={backdropUrl} strength={backdropStrength} />
      <HeroComponent
        tenant={tenant}
        catalogCount={catalogCount}
        onSearch={onSearch}
        onSelectCategory={onSelectCategory}
        onNavigate={onNavigate}
        featuredProduct={featuredProduct}
        categories={categories}
        heroTitle={heroTitle}
        heroSubtitle={heroSubtitle}
        heroCtaPrimary={resolvedHeroCtaPrimary}
        heroCtaSecondary={resolvedHeroCtaSecondary}
        {...props}
      />
      <HeroBackdropEditPill />
    </div>
  );
}
